import { Component, Fragment, PropsWithChildren } from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from '../../router/withRouter';
import arrayMutators from 'final-form-arrays';
import { Field } from 'react-final-form';
import { Col, Row } from 'react-bootstrap';
import { RootStore } from '../../store';
import UsersPageStore from './UsersPageStore';
import { SpotLoading, SpotSelectBox, SpotSelectOption, SpotSvg, SpotToggle } from '@enterprise/spot';
import { UserEntity } from '../../core/entity/UserEntity';
import { toJS } from 'mobx';
import { Form } from '@enterprise/common';
import { RouterStore } from '../../store';
import { FlyOver } from '../../components/flyover/FlyOver';
import OrganizationsPageStore from '../organizations/OrganizationsPageStore';
import { Notifications } from '../Notifications';
import { UserEditFormValues, UserFormType } from './formType/UserFormType';
import { FeaturesCheckboxes } from '../../components/formControls/FeaturesCheckboxes';
import { AnalyticsFeatureAccessCheckboxes } from '../organizations/formControls/AnalyticsFeatureAccessCheckboxes';
import { AnalyticsAccessType } from '../../core/entity/AnalyticsAccessEntity';
import { Trans } from 'react-i18next';
import { FEATURES } from '@enterprise/core';
import classNames from 'classnames';
import { UserEditSyncOrganizationPricingTier } from './UserEditSyncOrganizationPricingTier';

interface UsersPageProps {
    usersPage: UsersPageStore;
    organizationsPage: OrganizationsPageStore;
    routing: RouterStore;
    match: any;
}

interface UsersPageState {
    isLoaded: boolean;
}

@inject((allStores: RootStore) => ({
    usersPage: allStores.pages.usersPage,
    organizationsPage: allStores.pages.organizationsPage,
    routing: allStores.routing,
}))
@observer
class UsersDetailsPageComponent extends Component<PropsWithChildren<UsersPageProps>, UsersPageState> {
    state = {
        isLoaded: false,
    };

    private form = new UserFormType();
    // private data: any;

    get user(): UserEntity | undefined {
        return toJS(this.props.usersPage.user);
    }

    componentDidMount() {
        this.props.organizationsPage.load().then(async () => {
            await this.props.usersPage.fetchUser(this.props.match.params.id);
            this.setState({ isLoaded: true });
        });
    }

    private buildAnalyticsAccessIds(pricingTierId?: number, featureAccessIds: number[] = []): number[] {
        const ids: number[] = [];
        if (pricingTierId) {
            ids.push(pricingTierId);
        }
        ids.push(...featureAccessIds);
        return ids;
    }

    save = async ({ features = [], pricingTierId, analyticsAccessIds = [], ...data }: UserEditFormValues) => {
        if (!data.roles || data.roles.length === 0) {
            Notifications.error('At least one role should be specified');
            return;
        }
        try {
            const userId = data.id as string;
            await this.props.usersPage.updateUser(userId, data.roles, data.isActive, data.orgId);
            const accessIds = this.buildAnalyticsAccessIds(pricingTierId, analyticsAccessIds);
            await Promise.all([this.props.usersPage.saveUserAnalyticsAccess(userId, accessIds), this.props.usersPage.saveFeatures(userId, features)]);

            this.props.routing.push('/users');
        } catch (e) {
            Notifications.fromException(e);
        }
    };

    cancel = () => {
        this.props.routing.push(`/users`);
    };

    render() {
        const { usersPage, organizationsPage } = this.props;
        if (!this.state.isLoaded || !this.user) {
            return (
                <Fragment>
                    <SpotLoading />
                </Fragment>
            );
        }

        const access = usersPage.userAnalyticsAccess;
        const pricingTierId = access.find(({ type }) => type === AnalyticsAccessType.PricingTier)?.id;
        const analyticsAccessIds = access.filter(({ type }) => type === AnalyticsAccessType.FeatureAccess).map((a) => a.id);
        const catalog = usersPage.analyticsAccessCatalog;
        const pricingTierOptions = catalog.filter(({ type }) => type === AnalyticsAccessType.PricingTier).sort((a, b) => a.id - b.id);
        const analyticsFeatureAccessOptions = catalog
            .filter(({ type }) => type === AnalyticsAccessType.FeatureAccess)
            .sort((a, b) => a.name?.localeCompare(b.name));

        const { organizationPricingTier, organizationPricingTierLoading } = usersPage;

        const formData: UserEditFormValues = {
            ...this.user,
            features: usersPage.features,
            orgId: this.user?.organization?.id,
            pricingTierId,
            analyticsAccessIds,
        };

        return (
            <Fragment>
                <FlyOver
                    title="Manage User"
                    cancelAction={this.cancel}
                    body={
                        <Form
                            data={formData}
                            formType={this.form}
                            mutators={{
                                ...arrayMutators,
                            }}
                            onSubmit={this.save}
                            keepDirtyOnReinitialize={true}
                        >
                            {({ handleSubmit, form, pristine, invalid }) => {
                                const values = form.getState().values as UserEditFormValues;
                                const analyticsAccessDisabled = !values?.features?.some((f) => f.name === FEATURES.ANALYTICS);

                                return (
                                    <div>
                                        <div>
                                            <div className="fly-over-form">
                                                <span className={'fly-over-section-title'}>Status:</span>
                                                <div className={'fly-over-group'}>
                                                    <Row>
                                                        <Col className={'label-text'}>Active:</Col>
                                                        <Col>
                                                            <Field name="isActive" type="checkbox">
                                                                {({ input }) => {
                                                                    return <SpotToggle {...input} />;
                                                                }}
                                                            </Field>
                                                        </Col>
                                                    </Row>
                                                </div>

                                                <span className={'fly-over-section-title'}>User Details:</span>
                                                <div className={'fly-over-group'}>
                                                    <Row>
                                                        <Col className={'label-text'}>First Name:</Col>
                                                        <Col>{this.user?.profile.firstName}</Col>
                                                    </Row>
                                                    <Row>
                                                        <Col className={'label-text'}>Last Name:</Col>
                                                        <Col>{this.user?.profile.lastName}</Col>
                                                    </Row>
                                                    <Row>
                                                        <Col className={'label-text'}>Email:</Col>
                                                        <Col>{this.user?.username}</Col>
                                                    </Row>
                                                </div>

                                                <span className={'fly-over-section-title'}>Roles:</span>
                                                <div className={'fly-over-group'} style={{ marginTop: '10px' }}>
                                                    <fieldset className="spot-form__checkbox-group" role="group">
                                                        <div className="spot-form__checkbox-group-inner">
                                                            {['USER', 'ADMIN', 'SUPER_ADMIN'].map((role) => (
                                                                <label key={role} className="spot-form__checkbox">
                                                                    <Field
                                                                        className="spot-form__checkbox-input"
                                                                        name="roles"
                                                                        component="input"
                                                                        type="checkbox"
                                                                        value={role}
                                                                    />
                                                                    <span className="spot-form__checkbox-inner">
                                                                        <span className="spot-form__checkbox-visual">
                                                                            <SpotSvg
                                                                                icon="checkmark"
                                                                                className="spot-icon spot-form__checkbox-checked-icon"
                                                                            />
                                                                            <SpotSvg
                                                                                icon="checkbox-indeterminate"
                                                                                className="spot-icon spot-form__checkbox-indeterminate-icon"
                                                                            />
                                                                        </span>
                                                                        <span className="spot-form__checkbox-label"> {role} </span>
                                                                    </span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    </fieldset>
                                                </div>

                                                <span className={'fly-over-section-title'}>Organization:</span>
                                                <div className={'fly-over-group'} style={{ marginTop: '10px' }}>
                                                    <Field name="orgId">
                                                        {({ input, meta }) => (
                                                            <Row>
                                                                <Col xs={8}>
                                                                    <SpotSelectBox {...input} error={meta}>
                                                                        {organizationsPage.organizations.map((org) => (
                                                                            <SpotSelectOption key={org.id} value={org.id}>
                                                                                {org.name}
                                                                            </SpotSelectOption>
                                                                        ))}
                                                                    </SpotSelectBox>
                                                                </Col>
                                                            </Row>
                                                        )}
                                                    </Field>
                                                </div>

                                                <Row>
                                                    <Col xs={12}>
                                                        <span className={'fly-over-section-title'}>
                                                            <Trans i18nKey="common:features">Features</Trans>:
                                                        </span>
                                                        <div className={'fly-over-group'}>
                                                            <FeaturesCheckboxes name="features" />
                                                        </div>
                                                    </Col>
                                                </Row>

                                                <UserEditSyncOrganizationPricingTier orgId={values.orgId} usersPage={usersPage} />
                                                <span
                                                    className={classNames('fly-over-section-title', {
                                                        'fly-over-section-title--disabled': analyticsAccessDisabled,
                                                    })}
                                                    aria-disabled={analyticsAccessDisabled}
                                                >
                                                    <Trans i18nKey="common:analyticsAccess">Analytics Access</Trans>:
                                                </span>
                                                <div className="fly-over-group" aria-disabled={analyticsAccessDisabled}>
                                                    <Row style={{ marginTop: '16px' }}>
                                                        <Col
                                                            className={classNames('label-text', {
                                                                'label-text--disabled': analyticsAccessDisabled,
                                                            })}
                                                        >
                                                            <Trans i18nKey="common:pricingTier">Pricing Tier</Trans>
                                                        </Col>
                                                    </Row>
                                                    {/*<PricingTierFormControl pricingTiers={pricingTierOptions} disabled={analyticsAccessDisabled} />*/}
                                                    <Row style={{ marginTop: '8px' }}>
                                                        <Col
                                                            className={classNames({
                                                                'fly-over-section-title--disabled': analyticsAccessDisabled,
                                                            })}
                                                        >
                                                            <Trans i18nKey="common:organizationSelectedPricingTier">
                                                                Organization Selected Pricing Tier:{' '}
                                                            </Trans>
                                                            <b>
                                                                {organizationPricingTierLoading ? (
                                                                    <Trans i18nKey="common:loading">Loading...</Trans>
                                                                ) : (
                                                                    (organizationPricingTier?.name ?? '\u2014')
                                                                )}
                                                            </b>
                                                        </Col>
                                                    </Row>
                                                    <Row style={{ marginTop: '16px' }}>
                                                        <Col
                                                            className={classNames('label-text', {
                                                                'label-text--disabled': analyticsAccessDisabled,
                                                            })}
                                                        >
                                                            <Trans i18nKey="common:accessFeatures">Analytics Access features</Trans>
                                                        </Col>
                                                    </Row>
                                                    <Row>
                                                        <Col>
                                                            <AnalyticsFeatureAccessCheckboxes
                                                                name="analyticsAccessIds"
                                                                featureAccessItems={analyticsFeatureAccessOptions}
                                                                disabled={analyticsAccessDisabled}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="fly-over-actions">
                                            <div className="spot-modal__footer-cancel-button" onClick={this.cancel}>
                                                <Trans i18nKey="common:cancel">cancel</Trans>
                                            </div>
                                            <button
                                                disabled={pristine || invalid}
                                                className="spot-button spot-button--primary"
                                                onClick={handleSubmit}
                                            >
                                                <Trans i18nKey="common:saveChangesButton">Save Changes</Trans>
                                            </button>
                                        </div>
                                    </div>
                                );
                            }}
                        </Form>
                    }
                />
            </Fragment>
        );
    }
}

export const UsersDetailsPage = withRouter(UsersDetailsPageComponent as any);
